import { isDraftVarianceAction } from './varianceActionState.js';
import { bindingQuality } from './bindingQuality.js';
import { fileHealthOpenState } from './fileHealthDisplay.js';
import { isSyntheticBac } from './syntheticBac.js';

const actionText = (value) => (value === null || value === undefined ? '' : String(value).trim());
const SYNTHETIC_BAC_NON_BLOCKING_FILE_HEALTH_CODES = new Set(['CPQ_MISSING_BAC']);
const VARIANCE_BLOCKING_FILE_HEALTH_CODES = new Set(['CPQ_MISSING_BAC', 'CPQ_DUPLICATE_PRODUCT']);

const actionMoney = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const actionMutation = (action = {}) => {
  if (action.compiledMutationOverride) return action.compiledMutationOverride;
  if (action.actionType === 'replace_product') return 'replace';
  return actionText(action.compiledMutation || 'none');
};

const canOmitSalesforceAccount = (action = {}, lane = actionText(action.exportLane), mutation = actionMutation(action)) => (
  lane === 'cpq_peoplesoft_adjustment'
  && mutation === 'remove'
  && isSyntheticBac(action.bac)
);

const actionProductCode = (action = {}, mutation = actionMutation(action)) => (
  mutation === 'replace'
    ? actionText(action.replacementProductCode || action.customProductCode || action.productCode)
    : actionText(action.productCode || action.sourceProductCode || action.customProductCode)
);

const actionRationale = (action = {}) => [
  action.problem,
  action.fix,
  action.note,
  action.metadata?.productNote?.text,
].map(actionText).filter(Boolean).join(' ');

export const isCompletionBlockingAction = (action) => {
  if (isDraftVarianceAction(action) || action.exportLane === 'none') return false;
  const lane = actionText(action.exportLane);
  const mutation = actionMutation(action);
  if (!['gm_correction', 'cpq_peoplesoft_adjustment'].includes(lane)) return true;
  if (!['none', 'add', 'remove', 'adjust', 'replace'].includes(mutation)) return true;
  if (mutation !== 'none' && !actionProductCode(action, mutation)) return true;
  if (['add', 'adjust'].includes(mutation) && actionMoney(action.proposedPrice ?? action.customPrice) === null) return true;
  if (mutation === 'replace' && actionMoney(action.proposedPrice ?? action.customPrice ?? action.originalPrice) === null) return true;
  if (lane === 'cpq_peoplesoft_adjustment' && mutation === 'remove' && actionMoney(action.originalPrice ?? action.customPrice) === null) return true;
  if (actionText(action.actionType) === 'custom' && actionRationale(action).length < 8) return true;
  const quality = bindingQuality(action);
  return lane === 'cpq_peoplesoft_adjustment'
    && quality.missingSalesforceAccount
    && !canOmitSalesforceAccount(action, lane, mutation);
};

export const completionBlockingFileHealthRows = ({ bac, fileHealthRows = [] } = {}) => {
  const rows = Array.isArray(fileHealthRows) ? fileHealthRows.filter(fileHealthOpenState) : [];
  return rows.filter((finding) => {
    const code = actionText(finding.code).toUpperCase();
    if (!VARIANCE_BLOCKING_FILE_HEALTH_CODES.has(code)) return false;
    return !(isSyntheticBac(bac) && SYNTHETIC_BAC_NON_BLOCKING_FILE_HEALTH_CODES.has(code));
  });
};

const completionBlockingFileHealthSummaryCount = ({ bac, fileHealthSummary = null } = {}) => {
  const byCode = Array.isArray(fileHealthSummary?.byCode) ? fileHealthSummary.byCode : null;
  if (!byCode) return Number(fileHealthSummary?.open ?? 0);
  return byCode.reduce((sum, row = {}) => {
    const code = actionText(row.code).toUpperCase();
    if (!VARIANCE_BLOCKING_FILE_HEALTH_CODES.has(code)) return sum;
    if (isSyntheticBac(bac) && SYNTHETIC_BAC_NON_BLOCKING_FILE_HEALTH_CODES.has(code)) return sum;
    return sum + Number(row.open || 0);
  }, 0);
};

export const buildVarianceCompletionState = ({
  bac = '',
  mockMode = false,
  varianceActions = [],
  displayTotals = {},
  fileHealthSummary = null,
  fileHealthRows = [],
  fileHealthSummaryReady = false,
  fileHealthRowsReady = false,
  isVarianceActionBusy = false,
} = {}) => {
  const hasVarianceActions = varianceActions.length > 0;
  const actionsForReadiness = bac
    ? varianceActions.map((action) => (action.bac ? action : { ...action, bac }))
    : varianceActions;
  const hasUnsavedVarianceActions = actionsForReadiness.some(isDraftVarianceAction);
  const hasLoadedFileHealthRows = fileHealthRowsReady && Array.isArray(fileHealthRows);
  const blockingFileHealthRows = hasLoadedFileHealthRows
    ? completionBlockingFileHealthRows({ bac, fileHealthRows })
    : [];
  const openFileHealthCount = hasLoadedFileHealthRows
    ? blockingFileHealthRows.length
    : completionBlockingFileHealthSummaryCount({ bac, fileHealthSummary });
  const fileHealthReady = mockMode || fileHealthSummaryReady || fileHealthRowsReady;
  const exportBlockingActionCount = actionsForReadiness.filter(isCompletionBlockingAction).length;
  const projectedDeltaIsZero = Math.abs(Number(displayTotals.deltas?.gmVsCpq || 0)) < 0.01;
  const canMarkComplete = !hasUnsavedVarianceActions
    && projectedDeltaIsZero
    && openFileHealthCount === 0
    && fileHealthReady
    && exportBlockingActionCount === 0
    && !isVarianceActionBusy;
  return {
    canMarkComplete,
    exportBlockingActionCount,
    fileHealthReady,
    hasUnsavedVarianceActions,
    hasVarianceActions,
    openFileHealthCount,
    projectedDeltaIsZero,
  };
};
